import React from 'react';
import { Alert } from '../../services/faultIntelligenceService';

interface AlertDrilldownModalProps {
  alert: Alert | null;
  isOpen: boolean;
  onClose: () => void;
  onAcknowledge?: (alertId: string) => void;
  onResolve?: (alertId: string) => void;
}

const AlertDrilldownModal: React.FC<AlertDrilldownModalProps> = ({
  alert,
  isOpen,
  onClose,
  onAcknowledge,
  onResolve
}) => {
  if (!isOpen || !alert) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return 'text-red-600 bg-red-100';
      case 'High': return 'text-orange-600 bg-orange-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Alert Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(alert.severity)}`}>
              {alert.severity}
            </span>
            <span className="text-sm text-gray-500">{alert.type}</span>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{alert.title}</h3>
            <p className="text-gray-600 dark:text-gray-400">{alert.message}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Equipment ID</label>
              <p className="text-gray-900 dark:text-white">{alert.equipment_id || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <p className="text-gray-900 dark:text-white">{alert.status}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">First Seen</label>
              <p className="text-gray-900 dark:text-white">{new Date(alert.first_seen).toLocaleString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Seen</label>
              <p className="text-gray-900 dark:text-white">{new Date(alert.last_seen).toLocaleString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Occurrence Count</label>
              <p className="text-gray-900 dark:text-white">{alert.count}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Duration</label>
              <p className="text-gray-900 dark:text-white">{alert.duration} minutes</p>
            </div>
          </div>

          {alert.acknowledged_by && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
              <p className="text-sm">
                <span className="font-medium">Acknowledged by:</span> {alert.acknowledged_by}
                <br />
                <span className="font-medium">At:</span> {alert.acknowledged_at ? new Date(alert.acknowledged_at).toLocaleString() : 'N/A'}
              </p>
            </div>
          )}

          {alert.status === 'Active' && (
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => onAcknowledge?.(alert.alert_id)}
                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
              >
                Acknowledge
              </button>
              <button
                onClick={() => onResolve?.(alert.alert_id)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Resolve
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDrilldownModal;